import { useCallback, useEffect, useState } from "react";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { VkLoginButton } from "./VkLoginButton";
import {
  fetchAuthProviders,
  type AuthProviderDefinition,
  type AuthProviderName,
} from "./lib/providers";

type ProviderState =
  | { status: "loading" }
  | { status: "loaded"; providers: AuthProviderDefinition[] }
  | { status: "error" };

interface AuthProviderButtonsProps {
  loadProviders?: () => Promise<AuthProviderDefinition[]>;
  onProviderSelect?: (provider: AuthProviderName) => void;
}

export function AuthProviderButtons({
  loadProviders = fetchAuthProviders,
  onProviderSelect,
}: AuthProviderButtonsProps) {
  const [state, setState] = useState<ProviderState>({ status: "loading" });
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });

    loadProviders()
      .then((providers) => {
        if (active) {
          setState({ status: "loaded", providers });
        }
      })
      .catch(() => {
        if (active) {
          setState({ status: "error" });
        }
      });

    return () => {
      active = false;
    };
  }, [loadProviders, requestVersion]);

  const retry = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  if (state.status === "loading") {
    return (
      <div
        role="status"
        className="flex w-full items-center justify-center gap-3 rounded-2xl bg-canvas px-6 py-4 text-sm text-muted"
      >
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-action border-t-transparent" />
        Загружаем способы входа…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div
        role="alert"
        className="w-full rounded-2xl border border-[#F2C7C2] bg-[#FFF1EF] p-4 text-center text-sm text-[#A93027]"
      >
        <p>Не удалось загрузить способы входа.</p>
        <button
          type="button"
          onClick={retry}
          className="mt-3 font-semibold underline underline-offset-4"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  if (state.providers.length === 0) {
    return (
      <div
        role="status"
        className="w-full rounded-2xl bg-canvas p-4 text-center text-sm leading-6 text-muted"
      >
        Способы входа временно недоступны.
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {state.providers.map((provider) =>
        provider.name === "google" ? (
          <GoogleLoginButton
            key={provider.name}
            onBeforeLogin={() => onProviderSelect?.(provider.name)}
          />
        ) : (
          <VkLoginButton
            key={provider.name}
            onBeforeLogin={() => onProviderSelect?.(provider.name)}
          />
        )
      )}
    </div>
  );
}
